import { ChangeEvent, FC, useState } from 'react';
import { Col, Collapse, Modal, Popover, RadioChangeEvent, Row } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
    addNewOnboardingChildAllowances,
    changeChildAllowanceComment,
    clearOnboardingChildAllowances,
    clearOnboardingErrorsValidation,
    removeFileOnboardingChildAllowances,
    removeOnboardingChildAllowances,
    updateOnboardingChildAllowances,
    updateOnboardingChildAllowancesChildArray,
} from '../../../../redux/reducers/onboarding/sliceReducer';
import Button from '../../../../components/button/Button';
import ChildAllowanceDocumentSection from './ChildAllowanceDocumentSection';
import ChildAllowanceInformationSection from './ChildAllowanceInformationSection';
import { createMask } from '../../../../common/utils/inputMaskUtils';
import moment from '../../../../common/constants/moment';
import { commonYearDateFormat } from '../../../../common/constants/dateConstants';
import UploadDropzone from '../../../../components/upload-dropzone/UploadDropzone';
import { uploadFileOnboardingChildAllowances } from '../../../../redux/reducers/onboarding/tabs/childAllowancesAsyncThunk';
import DocumentIcon from '../../../../assets/icons/DocumentIcon';
import { CaretRightOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';

const genderParser: Record<'boy' | 'girl', string> = {
    boy: 'man',
    girl: 'woman',
};

const ChildAllowance: FC = () => {
    const dispatch = useAppDispatch();
    const { hasChildren, children, documents, notes } = useAppSelector((state) => state.onboarding.childAllowances);
    const errorMessages = useAppSelector((state) => state.onboarding.validationError.childAllowances);
    const authState = useAppSelector((state) => state.auth);
    const { consultantId } = useParams();
    const [selectedForRemoving, setSelectedForRemoving] = useState(0);
    const [activeKeys, setActiveKeys] = useState<string[]>([]);

    const onChangeRadio = (event: RadioChangeEvent): void => {
        const fieldValue = event.target.value;

        if (!fieldValue) {
            dispatch(clearOnboardingChildAllowances());
            dispatch(clearOnboardingErrorsValidation());
        }

        dispatch(updateOnboardingChildAllowances({ hasChildren: fieldValue }));
    };

    const onAddNewChild = (): void => {
        dispatch(addNewOnboardingChildAllowances());
    };

    const removeChild = (id: number): void => {
        dispatch(removeOnboardingChildAllowances(id));
        setSelectedForRemoving(0);
    };

    const onChangeChildInput =
        (id: number) =>
        (event: ChangeEvent<HTMLInputElement> | RadioChangeEvent): void => {
            const fieldName = event.target.name!;
            const fieldValue = event.target.value;

            let field;

            if (fieldName === 'SSNumber') {
                field = {
                    [fieldName]: createMask(fieldValue, children?.find((item) => item.id === id)?.SSNumber ?? ''),
                };
            } else {
                field = { [fieldName]: fieldValue };
            }

            dispatch(updateOnboardingChildAllowancesChildArray({ id, field }));
        };

    const onChangeChildDate =
        (id: number, fieldName: string) =>
        (date: Date | null): void => {
            const value = date ? moment(date)?.format(commonYearDateFormat) : '';
            const field = { [fieldName]: value };
            dispatch(updateOnboardingChildAllowancesChildArray({ id, field }));
        };

    const onChangeChildDropdown =
        (id: number, fieldName: string) =>
        (data: any): void => {
            const field =
                fieldName === 'gender' ? { [fieldName]: genderParser[data as 'boy' | 'girl'] } : { [fieldName]: data };
            dispatch(updateOnboardingChildAllowancesChildArray({ id, field }));
        };

    const getError = (fieldName: string): string => {
        if (errorMessages) {
            return errorMessages[fieldName] ?? '';
        }
        return '';
    };

    const onChangeNotes = (text: string) => {
        dispatch(changeChildAllowanceComment(text));
    };

    const childNumber = (children?.findIndex((child) => child.id === selectedForRemoving) ?? 0) + 1;

    const collapseItems: any = [
        {
            key: '1',
            label: 'Children information',
            extra: () => <span>Mandatory if you are subject to tax at source</span>,
            children: (
                <ChildAllowanceInformationSection
                    hasChildren={hasChildren}
                    getError={getError}
                    onAddNewChild={onAddNewChild}
                    onChangeRadio={onChangeRadio}
                    onChangeChildInput={onChangeChildInput}
                    onChangeChildDate={onChangeChildDate}
                    onChangeChildDropdown={onChangeChildDropdown}
                    onRemove={setSelectedForRemoving}
                    children={children}
                    onChangeNotes={onChangeNotes}
                    notes={notes}
                />
            ),
        },
    ];
    if (hasChildren) {
        collapseItems.push({
            key: '2',
            label: 'Documents',
            children: (
                <>
                    {' '}
                    {hasChildren && (
                        <div style={{ marginBottom: '1rem' }}>
                            <span> Birth certificate Mandatory (if child from 18-25 educational certificates)</span>
                            {/* <Popover
            className="info-pop-button"
            placement="rightTop"
            content={<DocumentInfContent />}
            trigger="hover"
            overlayClassName="info-pop-content"
            showArrow={false}>
            <button>
              <WarningIcon />
            </button>
          </Popover> */}
                        </div>
                    )}
                    {hasChildren && (
                        <UploadDropzone
                            defaultFiles={documents}
                            onSubmit={(files) =>
                                dispatch(
                                    uploadFileOnboardingChildAllowances({
                                        files,
                                        ownerId: consultantId || authState._id,
                                    })
                                )
                            }
                            removeFile={(file) => dispatch(removeFileOnboardingChildAllowances(file))}
                            disabled={!hasChildren}
                            errorMessage={errorMessages['data.documents'] ?? ''}
                            isDownload
                        />
                    )}
                </>
            ),
        });
    }
    if (hasChildren) {
        collapseItems.push({
            key: '3',
            label: 'Templates',
            children: (
                <>
                    {hasChildren && (
                        <div className="list-download-files-wrapper" style={{ marginTop: '20px' }}>
                            <h5>You can download templates for the family allowance:</h5>
                            <div className="list-download-files">
                                <Popover
                                    className="info-pop-button"
                                    placement="rightTop"
                                    content={<p>Click to download</p>}
                                    trigger="hover"
                                    overlayClassName="info-pop-content"
                                    showArrow={false}
                                >
                                    <a href="/files/Demande_AF_Eng_01.22.pdf" download>
                                        <div>
                                            <DocumentIcon className="download-icon" />
                                        </div>
                                        <span>English.pdf</span>
                                    </a>
                                </Popover>

                                <Popover
                                    className="info-pop-button"
                                    placement="rightTop"
                                    content={<p>Click to download</p>}
                                    trigger="hover"
                                    overlayClassName="info-pop-content"
                                    showArrow={false}
                                >
                                    <a href="/files/Demande_AF_FR_01.22.pdf" download>
                                        <div>
                                            <DocumentIcon className="download-icon" />
                                        </div>
                                        <span>French.pdf</span>
                                    </a>
                                </Popover>

                                <Popover
                                    className="info-pop-button"
                                    placement="rightTop"
                                    content={<p>Click to download</p>}
                                    trigger="hover"
                                    overlayClassName="info-pop-content"
                                    showArrow={false}
                                >
                                    <a href="/files/Demande_AF_DE_01.22.pdf" download>
                                        <div>
                                            <DocumentIcon className="download-icon" />
                                        </div>
                                        <span>German.pdf</span>
                                    </a>
                                </Popover>

                                <Popover
                                    className="info-pop-button"
                                    placement="rightTop"
                                    content={<p>Click to download</p>}
                                    trigger="hover"
                                    overlayClassName="info-pop-content"
                                    showArrow={false}
                                >
                                    <a href="/files/Demande_AF_IT_01.22.pdf" download>
                                        <div>
                                            <DocumentIcon className="download-icon" />
                                        </div>
                                        <span>Italian.pdf</span>
                                    </a>
                                </Popover>
                            </div>
                        </div>
                    )}
                </>
            ),
        });
    }

    const toggleAll = () => {
        if (activeKeys.length === collapseItems.length) {
            setActiveKeys([]);
        } else {
            setActiveKeys(collapseItems.map((item: any) => item.key));
        }
    };

    return (
        <div className="onboarding-wrapper-v2">
            <Row justify="end">
                <span className="toggle-button-onboarding" onClick={toggleAll}>
                    {activeKeys.length === collapseItems.length ? 'Collapse All' : 'Expand All'}{' '}
                    <CaretRightOutlined rotate={activeKeys.length === collapseItems.length ? 270 : 90} />
                </span>
            </Row>
            <Collapse
                activeKey={activeKeys}
                onChange={(keys) => setActiveKeys(keys as string[])}
                style={{ width: '100%' }}
                className="onboarding-collapse"
                expandIconPosition="right"
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 270 : 90} />}
            >
                {collapseItems.map((item: any) => (
                    <Collapse.Panel
                        key={item.key}
                        header={item.label}
                        extra={item.extra && item.extra()}
                        className={`${item.extra ? 'custom-collapse-panel' : ''}`}
                    >
                        {item.children}
                    </Collapse.Panel>
                ))}
            </Collapse>
            {!!selectedForRemoving && (
                <Modal
                    width={490}
                    open={!!selectedForRemoving}
                    centered
                    footer={null}
                    destroyOnClose={true}
                    wrapClassName="footer-edit-lead-modal-window-wrapper"
                >
                    <h4 style={{ marginBottom: 32 }}>Delete</h4>
                    <p>
                        Are you sure you want to delete <b>Child {childNumber}</b>? This action is irreversible.
                    </p>
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Button
                                buttonWidth="100%"
                                buttonSize="normal"
                                buttonStyle="secondary"
                                onClick={() => setSelectedForRemoving(0)}
                            >
                                Cancel
                            </Button>
                        </Col>

                        <Col span={12}>
                            <Button
                                buttonWidth="100%"
                                buttonSize="normal"
                                buttonStyle="primary"
                                onClick={() => removeChild(selectedForRemoving)}
                            >
                                Yes, delete
                            </Button>
                        </Col>
                    </Row>
                </Modal>
            )}
        </div>
    );
};

export default ChildAllowance;
